import React, { FC, useContext, useEffect, useMemo, useCallback, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import axios from "axios";
import "./Basket.css";
import Button from "../../component/UiLib/Button/Button";
import UnarchiveButtonPassive from "../../assets/unarchiveButtonPassive.svg";
import DeleteButtonPassive from "../../assets/deleteElementGreyTrue.svg";
import ArrowUp from "../../assets/arrow_up.svg";
import ArrowDown from "../../assets/arrow_down.svg";
import { CurrentTab, TabNavigator } from "../../component/UiLib/TabNavigator/TabNavigator";
import WidgetContainer from "../../component/UiLib/WidgetContainer/WidgetContainer";
import { Table, TableBody, TableHead, TableTd, TableTh, TableThSort, TableTr } from "../../component/UiLib/Table/Table";
import { Context } from "../../index";
import { fullNameFormat } from "../../helpers/Inicials";
import { dateToNiceString } from "../../helpers/dateToNiceString";
import { AppContext } from "../../App";
import { scrollYCheck } from "../../helpers/domUtils";
import { sortAny, SortType } from "../../helpers/sortUtils";
import ConfirmModal from "../../component/ModalsLib/ConfirmModal/ConfirmModal";
import { hasPermission } from "../../helpers/hasPermisson";
import { IViewBasketEntities } from "../../common/api_interfaces/basket/IViewBasketResp";
import { IViewBasket } from "../../common/api_interfaces/basket/IViewBasket";
import { BasketTypeOfEntity } from "../../common/enums/BasketTypeOfEntity";
import { InfiniteScroll } from "../../component/Shared/InfiniteScroll/InfiniteScroll";
import { handleError } from "../../helpers/ErrorLib/handleError";

interface TabData {
    name: string;
    code: BasketTypeOfEntity;
}

const TABLE_HEADERS: TabData[] = [
    { name: "Проект", code: BasketTypeOfEntity.project },
    { name: "Доска", code: BasketTypeOfEntity.board },
    { name: "Колонка", code: BasketTypeOfEntity.column },
    { name: "Задача", code: BasketTypeOfEntity.task },
    { name: "Файл", code: BasketTypeOfEntity.file },
];

const GLOBAL_TAKE = 30;

const Basket: FC = () => {
    const { store } = useContext(Context);
    const { showToast, setBreadCrumb, setNavPanelHighlight, systemUserAccess, getData } = useContext(AppContext);

    const [allDataEnd, setAllDataEnd] = useState(false);
    const [entityType, setEntityType] = useState<BasketTypeOfEntity>(BasketTypeOfEntity.project);
    const [basketData, setBasketData] = useState<IViewBasketEntities[]>([]);
    const [currentBasketData, setCurrentBasketData] = useState<IViewBasketEntities[]>([]);
    const [itemTo, setItemTo] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showModalDelete, setShowModalDelete] = useState(false);
    const isLoadingRef = useRef<boolean>();

    // Мемоизированные пермишены
    const permissions = useMemo(
        () => ({
            view: hasPermission(systemUserAccess, "BasketAction", "view"),
            edit: hasPermission(systemUserAccess, "BasketAction", "edit"),
            delete: hasPermission(systemUserAccess, "BasketAction", "delete"),
        }),
        [systemUserAccess]
    );

    // Установка хлебных крошек и подсветки навигации
    useEffect(() => {
        setBreadCrumb([{ label: "Корзина", url: "basket" }]);
        setNavPanelHighlight("basket");
    }, [setBreadCrumb, setNavPanelHighlight]);

    // Загрузка данных
    const fetchData = useCallback(
        async (skip: number) => {
            if ((allDataEnd && skip !== 0) || isLoadingRef.current) return;

            if (skip === 0) {
                setAllDataEnd(false);
            }

            try {
                isLoadingRef.current = true;
                const data: IViewBasket = {
                    skip,
                    take: GLOBAL_TAKE,
                    filters: { byEntityType: String(entityType) },
                    sort: { deletedAt: "asc" },
                };

                const response = await store.basketService.viewBasket(data);
                const getData = response.data;

                if (!getData?.entity?.length && skip !== 0) {
                    setAllDataEnd(true);
                    return;
                }

                if (skip === 0) {
                    setBasketData(getData.entity);
                    setCurrentBasketData(getData.entity);
                } else {
                    setBasketData((prev) => [...prev, ...getData.entity]);
                    setCurrentBasketData((prev) => [...prev, ...getData.entity]);
                }
            } catch (error) {
                handleError(error, { showToast });
            } finally {
                isLoadingRef.current = false;
            }
        },
        [allDataEnd, entityType, store.basketService, showToast]
    );

    // Загрузка данных при изменении типа entity
    useEffect(() => {
        if (permissions.view) {
            fetchData(0).catch((err) => handleError(err, { showToast }));
        }
    }, [entityType, permissions.view]);

    // Восстановление данных
    const restoreData = useCallback(
        async (ids: number[]) => {
            if (!ids.length) {
                showToast("Не выбран объект для восстановления!");
                return;
            }

            try {
                await store.basketService.restoreBasket(ids);
                await fetchData(0);
                await getData();
            } catch (error) {
                handleError(error, { showToast });
            }
        },
        [store.basketService, fetchData, handleError, showToast, getData]
    );

    // Удаление файлов из корзины
    const deleteBasketFiles = useCallback(
        async (ids: number[]) => {
            if (!ids.length) {
                showToast("Не выбраны объекты для удаления!");
                return;
            }

            try {
                await store.basketService.deleteBasketFiles(ids);
                await fetchData(0);
            } catch (error) {
                handleError(error, { showToast });
            }
        },
        [store.basketService, fetchData, handleError, showToast]
    );

    // Сортировка данных
    const handleSort = useCallback(
        (type: SortType, sortField: keyof IViewBasketEntities, id: number) => {
            if (id === 2) {
                setBasketData(currentBasketData);
                return;
            }

            const sortDirection = id === 0 ? "asc" : "desc";
            const sortedData = sortAny(type, basketData, sortDirection, sortField);

            if (sortedData) {
                setBasketData([...sortedData]);
            }
        },
        [basketData, currentBasketData]
    );

    // Смена таба
    const changeTab = useCallback((entity: BasketTypeOfEntity) => {
        setAllDataEnd(false);
        setCurrentBasketData([]);
        setBasketData([]);
        setEntityType(entity);
    }, []);

    // Модальное окно подтверждения
    const handleModalClose = useCallback(
        (confirmed: boolean) => {
            if (confirmed && itemTo) {
                restoreData([itemTo]);
            }
            setShowModal(false);
            setItemTo(null);
        },
        [itemTo, restoreData]
    );

    // Модальное окно подтверждения удаления
    const handleModalDeleteClose = useCallback(
        (confirmed: boolean) => {
            if (confirmed && itemTo) {
                deleteBasketFiles([itemTo]);
            }
            setShowModalDelete(false);
            setItemTo(null);
        },
        [itemTo, restoreData]
    );

    const openModal = useCallback((itemId: number) => {
        setItemTo(itemId);
        setShowModal(true);
    }, []);

    const openModalDelete = useCallback((itemId: number) => {
        setItemTo(itemId);
        setShowModalDelete(true);
    }, []);

    // Рендер строк таблицы
    const renderTableRows = useCallback(
        (items: IViewBasketEntities[], entityCode: BasketTypeOfEntity) => {
            return items.map((item, index) => (
                <TableTr key={item.id} style={{ display: "flex", marginBottom: "10px" }}>
                    <TableTd classBlock="page__basket--cell__main">{item.entityName}</TableTd>

                    {item.projectName && <TableTd classBlock="page__basket--cell__main">{item.projectName}</TableTd>}

                    <TableTd classBlock="page__basket--cell">
                        {fullNameFormat(
                            {
                                name: item.createdBy.name,
                                surname: item.createdBy.surname,
                                middlename: item.createdBy.middleName ?? "",
                            },
                            "s N M"
                        )}
                    </TableTd>

                    <TableTd classBlock="page__basket--cell">
                        {fullNameFormat(
                            {
                                name: item.deletedBy.name,
                                surname: item.deletedBy.surname,
                                middlename: item.deletedBy.middleName ?? "",
                            },
                            "s N M"
                        )}
                    </TableTd>

                    <TableTd classBlock="page__basket--cell">
                        {dateToNiceString(new Date(item.deletedAt), "yyyy-MM-dd")}
                    </TableTd>

                    <TableTd classBlock="page__basket--cell__reverse">
                        {(entityCode === BasketTypeOfEntity.file && item.deletedAtS3 === undefined) ||
                        entityCode !== BasketTypeOfEntity.file ? (
                            <Button
                                id={`${index}-recover`}
                                image={UnarchiveButtonPassive}
                                callBack={() =>
                                    permissions.edit
                                        ? openModal(item.id)
                                        : showToast("У вас нет прав для восстановления объекта!")
                                }
                            />
                        ) : null}
                    </TableTd>

                    {entityCode === BasketTypeOfEntity.file && (
                        <TableTd classBlock="page__basket--cell__reverse">
                            {item.deletedAtS3 === undefined && (
                                <Button
                                    id={`${index}-delete-s3`}
                                    image={DeleteButtonPassive}
                                    callBack={() =>
                                        permissions.delete
                                            ? openModalDelete(item.id)
                                            : showToast("У вас нет прав для удаления файла!")
                                    }
                                />
                            )}
                        </TableTd>
                    )}
                </TableTr>
            ));
        },
        [deleteBasketFiles, openModal, permissions.delete, permissions.edit, showToast]
    );

    // Рендер табов
    const renderTabs = useMemo(() => {
        return TABLE_HEADERS.map((tab) => (
            <CurrentTab key={tab.code} name={tab.name} callBack={() => changeTab(tab.code)}>
                <WidgetContainer style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                    <Table style={{ tableLayout: "fixed", borderSpacing: "12px 12px", width: "calc(100% - 8px)" }}>
                        <TableHead>
                            <TableTr style={{ display: "flex" }}>
                                <TableThSort
                                    classBlock="page__basket--cell__main"
                                    images={[ArrowUp, ArrowDown]}
                                    callBack={(id) => handleSort(SortType.text, "entityName", id)}
                                >
                                    {tab.name.toUpperCase()}
                                </TableThSort>

                                {tab.code !== BasketTypeOfEntity.project && (
                                    <TableThSort
                                        classBlock="page__basket--cell__main"
                                        images={[ArrowUp, ArrowDown]}
                                        callBack={(id) => handleSort(SortType.text, "projectName", id)}
                                    >
                                        ПРОЕКТ
                                    </TableThSort>
                                )}

                                <TableThSort
                                    classBlock="page__basket--cell"
                                    images={[ArrowUp, ArrowDown]}
                                    callBack={(id) => handleSort(SortType.text, "createdBy", id)}
                                >
                                    РУКОВОДИТЕЛЬ
                                </TableThSort>

                                <TableThSort
                                    classBlock="page__basket--cell"
                                    images={[ArrowUp, ArrowDown]}
                                    callBack={(id) => handleSort(SortType.text, "deletedBy", id)}
                                >
                                    УДАЛИЛ
                                </TableThSort>

                                <TableThSort
                                    classBlock="page__basket--cell"
                                    images={[ArrowUp, ArrowDown]}
                                    callBack={(id) => handleSort(SortType.text, "deletedAt", id)}
                                >
                                    ДАТА УДАЛЕНИЯ
                                </TableThSort>

                                <TableTd classBlock="page__basket--cell__reverse" />

                                {tab.code === BasketTypeOfEntity.file && (
                                    <TableTh classBlock="page__basket--cell__reverse" />
                                )}
                            </TableTr>
                        </TableHead>
                    </Table>

                    <div className="page__basket--scroll">
                        <Table style={{ tableLayout: "fixed", borderSpacing: "12px 12px", width: "100%" }}>
                            <TableBody>
                                {renderTableRows(basketData, tab.code)}
                                <InfiniteScroll<HTMLTableRowElement>
                                    hasMore={!allDataEnd}
                                    isLoading={isLoadingRef.current == true}
                                    onLoadMore={() => fetchData(basketData.length)}
                                    render={(ref) => <tr ref={ref}></tr>}
                                />
                            </TableBody>
                        </Table>
                    </div>
                </WidgetContainer>
            </CurrentTab>
        ));
    }, [basketData, changeTab, handleSort, renderTableRows, allDataEnd, fetchData]);

    if (!permissions.view) {
        return null;
    }

    return (
        <div className="page__basket">
            {showModal && (
                <ConfirmModal
                    className="basket-height-current"
                    callBack={handleModalClose}
                    confirmText="Да"
                    cancelText="Нет"
                    headText="Восстановление сущности"
                    bodyText="Вы уверены, что хотите восстановить сущность?"
                />
            )}

            {showModalDelete && (
                <ConfirmModal
                    className="basket-height-current"
                    callBack={handleModalDeleteClose}
                    confirmText="Да"
                    cancelText="Нет"
                    headText="Удаление файла"
                    bodyText="Вы уверены, что хотите удалить файл из системы?"
                />
            )}

            <TabNavigator queryParamName="tab" callbackOnLoadFromQuery>
                {renderTabs}
            </TabNavigator>
        </div>
    );
};

export default observer(Basket);
